import { AvailabilitySlot } from '../../../../../../components/shared/AvailabilitySlots/AvailabilitySlot';
import { Button, Flexbox, FlexItem, Text, useResize } from '@morf/ui';
import { FC, memo, useState } from 'react';
import { WeekdayDetailsSlotsProps } from './types';
import { useResizeHandler } from '../../../../../../components/helpers/useResizeHandler';
import { useTheme } from '@morf/theming';

const WeekdayDetailsSlots: FC<WeekdayDetailsSlotsProps> = ({
  columns,
  isLoading,
  weekdayAvailabilitySlots,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const gap = 0.5;
  const spacing = (columns - 1) * gap;
  const columnWidth = `calc((100% - ${spacing}rem) / ${columns})`;

  const responsiveSlots = useResize({
    slots: theme.maxNrOfSlots,
  });

  const maxNrOfSlots = parseInt(responsiveSlots.slots);

  const showMoreButton =
    !isExpanded && weekdayAvailabilitySlots.length > maxNrOfSlots;

  const sortedWeekdayAvailabilitySlots = weekdayAvailabilitySlots.sort(
    (a, b) => {
      // First compare by seconds
      if (a.slot.seconds.low < b.slot.seconds.low) return -1;
      if (a.slot.seconds.low > b.slot.seconds.low) return 1;

      // If seconds are equal, compare by nanoseconds
      if (a.slot.nanoseconds < b.slot.nanoseconds) return -1;
      if (a.slot.nanoseconds > b.slot.nanoseconds) return 1;

      return 0; // Equal timestamps
    }
  );

  const visibleWeekdayAvailabilitySlots = isExpanded
    ? sortedWeekdayAvailabilitySlots
    : sortedWeekdayAvailabilitySlots.slice(0, maxNrOfSlots);

  const handleShowLessClick = () => setIsExpanded(false);
  const handleShowMoreClick = () => setIsExpanded(true);

  useResizeHandler([isExpanded]);

  return (
    <Flexbox
      justifyContent='flex-start'
      alignItems='center'
      flexWrap='wrap'
      height='auto'
      gap={gap}
    >
      {visibleWeekdayAvailabilitySlots.map((availabilitySlot, index) => {
        return (
          <FlexItem key={index} flexBasis={columnWidth}>
            <AvailabilitySlot
              availabilitySlot={availabilitySlot}
              isLoading={isLoading}
            />
          </FlexItem>
        );
      })}

      {showMoreButton && (
        <Button
          variant='tertiary'
          width={columnWidth}
          onClick={handleShowMoreClick}
        >
          <Text tag='h5' color='inherit'>
            Show more
          </Text>
        </Button>
      )}

      {isExpanded && (
        <Button
          variant='tertiary'
          width={columnWidth}
          onClick={handleShowLessClick}
        >
          <Text tag='h5' color='inherit'>
            Show less
          </Text>
        </Button>
      )}
    </Flexbox>
  );
};

export const MemoizedWeekdayDetailsSlots = memo(WeekdayDetailsSlots);
